import { Action } from 'redux';

import { isRecommendationHiddenAction, isRecommendationUnhiddenAction } from '../../app/actions/spaces/HideRecommendationAction';
import { ListResult } from '../../ts/fetchers/ListResult';
import { ExplorableItemType } from '../../ts/models/ContentType';
import { IContentInfo } from '../../ts/models/IContentInfo';
import { isHomeAddFavoriteSucceededAction, ishomeFetchFavoritesSucceededAction, ishomeFetchPopularSucceededAction, ishomeFetchRecentsSucceededAction, ishomeFetchRecommendationsSucceededAction, isHomeRemoveFavoriteSucceededAction } from '../actions/HomeActions';
import { ChannelState, RecommendationChannelState } from '../types/HomeTypes';
import { normalizedContentKey } from './NormalizedContentReducer';

const initialChannelState: ChannelState = {
  isFetching: true, //TODO: isFetching should be false in initialState, and should be set to true by redux reducer that listens to new HomeFavoritesRequested action
  items: [],
};

function deriveChannelStateFromResult(result: ListResult<IContentInfo>): ChannelState {
  const derivedChannelState: ChannelState = {
    items: result.items.map((item: IContentInfo) => normalizedContentKey(item.id, item.contentType)),
    isFetching: false,
  };

  return derivedChannelState;
}

function derivedRecommendationChannelStateFromAction(previousState: RecommendationChannelState,
                                                     contentId: string,
                                                     contentType: ExplorableItemType,
                                                     hidden: boolean): RecommendationChannelState {
  const key: string = normalizedContentKey(contentId, contentType);
  if (previousState.recommendedViewMap && previousState.recommendedViewMap.has(key)) {
    const recommendedViewMap = previousState.recommendedViewMap.set(key, hidden);
    return {
      ...previousState,
      items: Array.from(recommendedViewMap.keys()).filter(k => recommendedViewMap.get(k) === false),
      recommendedViewMap
    };
  }

  return previousState;
}

export function favoritesContentReducer(previousState: ChannelState = {...initialChannelState}, action: Action) {
  if (ishomeFetchFavoritesSucceededAction(action)) {
    return deriveChannelStateFromResult(action.result);
  }
  if(isHomeAddFavoriteSucceededAction(action)) {
    const { contentId, contentType } = action;
    const key: string = normalizedContentKey(contentId, contentType);
    // if item already exists in the favorites list, prevent duplicate addition
    if(previousState.items.some((favoriteItemKey: string) => favoriteItemKey === key)) {
      return previousState;
    }
    return {
      ...previousState,
      items: [normalizedContentKey(contentId, contentType), ...previousState.items]
    };
  }
  if(isHomeRemoveFavoriteSucceededAction(action)) {
    const { contentId, contentType } = action;
    const key: string = normalizedContentKey(contentId, contentType);
    return {
      ...previousState,
      items: previousState.items.filter((contentId: string) => contentId !== key)
    };
  }
  return previousState;
}

export function recommendationsContentReducer(previousState: RecommendationChannelState = {...initialChannelState}, action: Action) {
  if (ishomeFetchRecommendationsSucceededAction(action)) {
    const derivedChannelState = deriveChannelStateFromResult(action.result);
    return {
      ...derivedChannelState,
      recommendedViewMap: new Map(action.result.items.map((item: IContentInfo) => [normalizedContentKey(item.id, item.contentType), false]) as Array<[string, boolean]>),
    };
  }

  if(isRecommendationHiddenAction(action)) {
    return derivedRecommendationChannelStateFromAction(previousState, action.contentId, action.contentType, true);
  }
  if(isRecommendationUnhiddenAction(action)) {
    return derivedRecommendationChannelStateFromAction(previousState, action.contentId, action.contentType, false);
  }
  return previousState;
}

export function recentsContentReducer(previousState: ChannelState = {...initialChannelState}, action: Action) {
  if (ishomeFetchRecentsSucceededAction(action)) {
    return deriveChannelStateFromResult(action.result);
  }
  return previousState;
}

export function popularContentReducer(previousState: ChannelState = {...initialChannelState}, action: Action) {
  if (ishomeFetchPopularSucceededAction(action)) {
    return deriveChannelStateFromResult(action.result);
  }
  return previousState;
}
